import React, { useState } from 'react'
import { IconCurrentLocation, IconSearch } from '@tabler/icons-react'
import { CustomLocation } from 'shared-types'
import { Button } from '~/components/Button'
import { LocationSearch } from '~/components/LocationSearch'
import Typography, {
  TypographyTag,
  TypographyVariant,
} from '~/components/Typography'
import { useUserLocation } from '~/hooks/useUserLocation'
import { GTM, GTMEvent } from '~/lib'
import { StoreSearchFormProps } from './StoreSearchForm.types'

const StoreSearchForm = ({
  label,
  placeholder,
  defaultValue,
  showSearchIcon = false,
  onLocate,
}: StoreSearchFormProps) => {
  const { geolocate, isGeolocating } = useUserLocation()
  const [errorMsg, setErrorMsg] = useState(null)

  const handleChangePlace = (location: CustomLocation) => {
    if (!location?.suburb) {
      return
    }

    setErrorMsg(null)
    GTM.dispatch(GTMEvent.STORE_FINDER, location.suburb)
    if (onLocate) {
      onLocate(
        {
          latitude: Number(location.latitude),
          longitude: Number(location.longitude),
        },
        location.postcode
      )
    }
  }

  const handleUserGeolocate = () => {
    if (geolocate && onLocate) {
      geolocate().then((position) => {
        if (!position) {
          setErrorMsg('Unable to get your location')
          return
        }
        const { latitude, longitude } = position
        onLocate({ latitude, longitude })
      })
    }
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
      }}>
      <div className='text-primary-heading mb-2'>
        <Typography
          tag={TypographyTag.p}
          variant={TypographyVariant.BodySmallBold}>
          {label}
        </Typography>
      </div>
      <LocationSearch
        placeholder={placeholder || `Enter ${label}`}
        name='location'
        type='postcode'
        defaultValue={defaultValue}
        prefix={
          showSearchIcon ? (
            <IconSearch size={20} className='text-primary-heading' />
          ) : (
            ''
          )
        }
        suffix={
          <Button
            size='xs'
            variant='ghost'
            icon={<IconCurrentLocation />}
            iconOnly
            onClick={handleUserGeolocate}
            loading={isGeolocating}
          />
        }
        onLocationSelected={(location?: CustomLocation) => {
          handleChangePlace(location)
        }}
      />
      {errorMsg && (
        <Typography
          tag={TypographyTag.p}
          variant={TypographyVariant.BodySmall}
          className='text-state-error mt-1'>
          {errorMsg}
        </Typography>
      )}
    </form>
  )
}

export default StoreSearchForm
